import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ComplianceForm = ({ recipient, quote, onNext, onBack, complianceData, onComplianceUpdate }) => {
  const [formData, setFormData] = useState({
    sourceOfFunds: complianceData?.sourceOfFunds || '',
    transferPurpose: complianceData?.transferPurpose || '',
    relationship: complianceData?.relationship || '',
    occupation: complianceData?.occupation || '',
    expectedFrequency: complianceData?.expectedFrequency || '',
    additionalInfo: complianceData?.additionalInfo || '',
    confirmations: {
      beneficiaryKnown: complianceData?.confirmations?.beneficiaryKnown || false,
      legitimatePurpose: complianceData?.confirmations?.legitimatePurpose || false,
      noThirdParty: complianceData?.confirmations?.noThirdParty || false,
      accurateInfo: complianceData?.confirmations?.accurateInfo || false
    }
  });

  const [errors, setErrors] = useState({});

  const sourceOfFundsOptions = [
    { value: 'salary', label: 'Salary/Employment Income' },
    { value: 'business', label: 'Business Income' },
    { value: 'savings', label: 'Personal Savings' },
    { value: 'investment', label: 'Investment Returns' },
    { value: 'gift', label: 'Gift from Family/Friends' },
    { value: 'loan', label: 'Loan Proceeds' },
    { value: 'inheritance', label: 'Inheritance' },
    { value: 'other', label: 'Other (specify in additional info)' }
  ];

  const transferPurposeOptions = [
    { value: 'family_support', label: 'Family Support/Maintenance' },
    { value: 'education', label: 'Education Expenses' },
    { value: 'medical', label: 'Medical Expenses' },
    { value: 'business', label: 'Business Transaction' },
    { value: 'property', label: 'Property Purchase/Investment' },
    { value: 'gift', label: 'Gift' },
    { value: 'loan_repayment', label: 'Loan Repayment' },
    { value: 'travel', label: 'Travel Expenses' },
    { value: 'other', label: 'Other (specify in additional info)' }
  ];

  const relationshipOptions = [
    { value: 'family', label: 'Family Member' },
    { value: 'friend', label: 'Friend' },
    { value: 'business_partner', label: 'Business Partner' },
    { value: 'employee', label: 'Employee' },
    { value: 'service_provider', label: 'Service Provider' },
    { value: 'myself', label: 'Myself (own account)' },
    { value: 'other', label: 'Other' }
  ];

  const frequencyOptions = [
    { value: 'one_time', label: 'One-time Transfer' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' },
    { value: 'irregular', label: 'Irregular/As Needed' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleConfirmationChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      confirmations: {
        ...prev?.confirmations,
        [field]: checked
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.sourceOfFunds) {
      newErrors.sourceOfFunds = 'Source of funds is required';
    }

    if (!formData?.transferPurpose) {
      newErrors.transferPurpose = 'Transfer purpose is required';
    }

    if (!formData?.relationship) {
      newErrors.relationship = 'Relationship to recipient is required';
    }

    if (!formData?.occupation) {
      newErrors.occupation = 'Occupation is required';
    }

    if (!formData?.expectedFrequency) {
      newErrors.expectedFrequency = 'Expected frequency is required';
    }

    // Check all confirmations
    const confirmations = formData?.confirmations;
    if (!confirmations?.beneficiaryKnown || !confirmations?.legitimatePurpose || 
        !confirmations?.noThirdParty || !confirmations?.accurateInfo) {
      newErrors.confirmations = 'All confirmations must be checked';
    }

    // Additional validation for high-value transfers
    if (parseFloat(quote?.sendAmount) > 10000) {
      if (!formData?.additionalInfo || formData?.additionalInfo?.length < 20) {
        newErrors.additionalInfo = 'Additional information is required for high-value transfers (minimum 20 characters)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onComplianceUpdate(formData);
      onNext();
    }
  };

  const getRiskLevel = () => {
    let riskScore = 0;
    
    // Amount-based risk
    const amount = parseFloat(quote?.sendAmount) || 0;
    if (amount > 10000) riskScore += 3;
    else if (amount > 5000) riskScore += 2;
    else if (amount > 1000) riskScore += 1;

    // Purpose-based risk
    if (['business', 'property', 'loan_repayment']?.includes(formData?.transferPurpose)) {
      riskScore += 2;
    }

    // Frequency-based risk
    if (formData?.expectedFrequency === 'monthly') {
      riskScore += 1;
    }

    if (riskScore >= 5) return { level: 'high', color: 'text-error', bg: 'bg-error/10' };
    if (riskScore >= 3) return { level: 'medium', color: 'text-warning', bg: 'bg-warning/10' };
    return { level: 'low', color: 'text-success', bg: 'bg-success/10' };
  };

  const riskAssessment = getRiskLevel();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Compliance Information</h2>
          <p className="text-muted-foreground mt-1">
            Please provide additional details for regulatory compliance
          </p>
        </div>
        <Button variant="ghost" onClick={onBack} iconName="ArrowLeft" iconPosition="left">
          Back
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Compliance Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Source of Funds */}
          <div>
            <Select
              label="Source of Funds"
              description="Where is the money coming from?"
              options={sourceOfFundsOptions}
              value={formData?.sourceOfFunds}
              onChange={(value) => handleInputChange('sourceOfFunds', value)}
              error={errors?.sourceOfFunds}
              required
            />
          </div>

          {/* Transfer Purpose */}
          <div>
            <Select
              label="Purpose of Transfer"
              description="What is this transfer for?"
              options={transferPurposeOptions}
              value={formData?.transferPurpose}
              onChange={(value) => handleInputChange('transferPurpose', value)}
              error={errors?.transferPurpose}
              required
            />
          </div>

          {/* Relationship */}
          <div>
            <Select
              label="Relationship to Recipient"
              description="How do you know the recipient?"
              options={relationshipOptions}
              value={formData?.relationship}
              onChange={(value) => handleInputChange('relationship', value)}
              error={errors?.relationship}
              required
            />
          </div>

          {/* Occupation */}
          <div>
            <Input
              label="Your Occupation"
              description="What is your current occupation?"
              placeholder="e.g., Software Engineer, Teacher, Business Owner"
              value={formData?.occupation}
              onChange={(e) => handleInputChange('occupation', e?.target?.value)}
              error={errors?.occupation}
              required
            />
          </div>

          {/* Expected Frequency */}
          <div>
            <Select
              label="Expected Transfer Frequency"
              description="How often do you plan to send money to this recipient?"
              options={frequencyOptions}
              value={formData?.expectedFrequency}
              onChange={(value) => handleInputChange('expectedFrequency', value)}
              error={errors?.expectedFrequency}
              required
            />
          </div>

          {/* Additional Information */}
          <div>
            <Input
              label="Additional Information"
              description={parseFloat(quote?.sendAmount) > 10000 ? "Required for high-value transfers" : "Optional additional details"}
              placeholder="Provide any additional context about this transfer..."
              value={formData?.additionalInfo}
              onChange={(e) => handleInputChange('additionalInfo', e?.target?.value)}
              error={errors?.additionalInfo}
              required={parseFloat(quote?.sendAmount) > 10000}
            />
          </div>

          {/* Confirmations */}
          <div className="bg-muted/50 border border-border rounded-lg p-6">
            <h3 className="font-medium text-foreground mb-4">Required Confirmations</h3>
            <div className="space-y-4">
              <Checkbox
                label="I personally know the beneficiary and can confirm their identity"
                checked={formData?.confirmations?.beneficiaryKnown}
                onChange={(e) => handleConfirmationChange('beneficiaryKnown', e?.target?.checked)}
              />
              <Checkbox
                label="This transfer is for a legitimate purpose and I am not being coerced"
                checked={formData?.confirmations?.legitimatePurpose}
                onChange={(e) => handleConfirmationChange('legitimatePurpose', e?.target?.checked)}
              />
              <Checkbox
                label="I am not sending money on behalf of a third party"
                checked={formData?.confirmations?.noThirdParty}
                onChange={(e) => handleConfirmationChange('noThirdParty', e?.target?.checked)}
              />
              <Checkbox
                label="All information provided is accurate and complete"
                checked={formData?.confirmations?.accurateInfo}
                onChange={(e) => handleConfirmationChange('accurateInfo', e?.target?.checked)}
              />
            </div>
            {errors?.confirmations && (
              <p className="text-error text-sm mt-2">{errors?.confirmations}</p>
            )}
          </div>
        </div>

        {/* Risk Assessment & Summary */}
        <div className="space-y-6">
          {/* Risk Assessment */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-medium text-foreground mb-4">Risk Assessment</h3>
            <div className={`${riskAssessment?.bg} rounded-lg p-4`}>
              <div className="flex items-center space-x-2 mb-2">
                <Icon 
                  name={riskAssessment?.level === 'high' ? 'AlertTriangle' : 
                        riskAssessment?.level === 'medium' ? 'AlertCircle' : 'CheckCircle'} 
                  size={20} 
                  className={riskAssessment?.color} 
                />
                <span className={`font-medium capitalize ${riskAssessment?.color}`}>
                  {riskAssessment?.level} Risk
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {riskAssessment?.level === 'high' && 'This transfer requires additional review and may take longer to process.'}
                {riskAssessment?.level === 'medium' && 'This transfer may require additional verification.'}
                {riskAssessment?.level === 'low' && 'This transfer meets standard compliance requirements.'}
              </p>
            </div>
          </div>

          {/* Transfer Summary */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-medium text-foreground mb-4">Transfer Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">To:</span>
                <span className="font-medium">{recipient?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-medium">${quote?.sendAmount} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">They Receive:</span>
                <span className="font-medium">{quote?.receiveAmount} {recipient?.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transfer Type:</span>
                <span className="font-medium capitalize">{quote?.transferType}</span>
              </div>
            </div>
          </div>

          {/* Compliance Checklist */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-medium text-foreground mb-4">Compliance Checklist</h3>
            <div className="space-y-2">
              {[
                { label: 'Source of funds verified', completed: !!formData?.sourceOfFunds },
                { label: 'Transfer purpose documented', completed: !!formData?.transferPurpose },
                { label: 'Relationship confirmed', completed: !!formData?.relationship },
                { label: 'All confirmations checked', completed: Object.values(formData?.confirmations)?.every(Boolean) }
              ]?.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon 
                    name={item?.completed ? "CheckCircle" : "Circle"} 
                    size={16} 
                    className={item?.completed ? "text-success" : "text-muted-foreground"} 
                  />
                  <span className={`text-sm ${item?.completed ? "text-foreground" : "text-muted-foreground"}`}>
                    {item?.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Continue Button */}
      <div className="flex justify-end pt-6 border-t border-border">
        <Button
          onClick={handleSubmit}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Review
        </Button>
      </div>
    </div>
  );
};

export default ComplianceForm;